import React, { forwardRef, useImperativeHandle } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useNewThread } from '../../../contexts/NewThreadContext';
import CanvasAssets from './CanvasAssets';
import {
  AIReportingProvider,
  useAIReporting,
  AIReportingTreeNode,
} from './AIReportingWrapper';
import {
  ThreadLayoutType,
  BoundProDoc,
  SubThreadInfo,
  ProjectAppTab,
} from '@dreamai/types';
import './chat-canvas.scss';

// Interface for ChatCanvas ref methods
export interface ChatCanvasRef {
  getTree: () => AIReportingTreeNode[];
}

// Props interface for ChatCanvas
interface ChatCanvasProps {
  layoutType?: ThreadLayoutType;
  discussingObject?: string;
  discussingObjectMatch?: Record<string, any>;
  isMainThread?: boolean;
  isChannelThread?: boolean;
  isOnboardingThread?: boolean;
  isInboundEmailThread?: boolean;
  agentType?: string;
  boundProDocs?: BoundProDoc[];
  subThreads?: SubThreadInfo[];
  projectApps?: ProjectAppTab[];
  threadDescription?: string;
  layoutLoading?: boolean;
  onHasAssets?: (hasAssets: boolean) => void;
  onMobileBack?: () => void;
  onMobileOpen?: () => void;
  hideMobileChatButton?: boolean;
  iconImageUrl?: string;
}

// Props interface for CanvasAssetsWithReporting
interface CanvasAssetsWithReportingProps {
  threadId: string;
  layoutType: ThreadLayoutType;
  discussingObject?: string;
  discussingObjectMatch?: Record<string, any>;
  isMainThread?: boolean;
  isChannelThread?: boolean;
  isOnboardingThread?: boolean;
  isInboundEmailThread?: boolean;
  iconImageUrl?: string;
  agentType?: string;
  boundProDocs?: BoundProDoc[];
  subThreads?: SubThreadInfo[];
  projectApps?: ProjectAppTab[];
  threadDescription?: string;
  layoutLoading?: boolean;
  onHasAssets?: (hasAssets: boolean) => void;
  onMobileBack?: () => void;
  onMobileOpen?: () => void;
  hideMobileChatButton?: boolean;
}

// CanvasAssets wrapper that has access to AIReporting context
const CanvasAssetsWithReporting = forwardRef<
  { getTree: () => AIReportingTreeNode[] },
  CanvasAssetsWithReportingProps
>(
  (
    {
      threadId,
      layoutType,
      discussingObject,
      discussingObjectMatch,
      isMainThread,
      isChannelThread,
      isOnboardingThread,
      isInboundEmailThread,
      agentType,
      boundProDocs,
      subThreads,
      projectApps,
      threadDescription,
      layoutLoading,
      onHasAssets,
      onMobileBack,
      onMobileOpen,
      hideMobileChatButton,
      iconImageUrl,
    },
    ref,
  ) => {
    const aiReporting = useAIReporting();

    // Expose getTree method to parent via ref
    useImperativeHandle(
      ref,
      () => ({
        getTree: () => aiReporting.getTree(),
      }),
      [aiReporting],
    );

    return (
      <CanvasAssets
        threadId={threadId}
        layoutType={layoutType}
        discussingObject={discussingObject}
        discussingObjectMatch={discussingObjectMatch}
        isMainThread={isMainThread}
        isChannelThread={isChannelThread}
        isOnboardingThread={isOnboardingThread}
        isInboundEmailThread={isInboundEmailThread}
        agentType={agentType}
        boundProDocs={boundProDocs}
        subThreads={subThreads}
        projectApps={projectApps}
        threadDescription={threadDescription}
        layoutLoading={layoutLoading}
        onHasAssets={onHasAssets}
        onMobileBack={onMobileBack}
        onMobileOpen={onMobileOpen}
        hideMobileChatButton={hideMobileChatButton}
        iconImageUrl={iconImageUrl}
      />
    );
  },
);
CanvasAssetsWithReporting.displayName = 'CanvasAssetsWithReporting';

/**
 * ChatCanvas Component
 *
 * Container for the chat canvas that displays workbox items for the current thread
 */
const ChatCanvas = forwardRef<ChatCanvasRef, ChatCanvasProps>(
  (
    {
      layoutType = 'tab',
      discussingObject,
      discussingObjectMatch,
      isMainThread,
      isChannelThread,
      isOnboardingThread,
      isInboundEmailThread,
      agentType,
      boundProDocs,
      subThreads,
      projectApps,
      threadDescription,
      layoutLoading,
      onHasAssets,
      onMobileBack,
      onMobileOpen,
      hideMobileChatButton,
      iconImageUrl,
    },
    ref,
  ) => {
    const [searchParams] = useSearchParams();
    const { newThreadId } = useNewThread();
    const threadId = searchParams.get('thread_id') || newThreadId;
    const aiReportingRef = React.useRef<{
      getTree: () => AIReportingTreeNode[];
    }>(null);

    // Expose getTree method to parent components
    useImperativeHandle(
      ref,
      () => ({
        getTree: () => aiReportingRef.current?.getTree() || [],
      }),
      [],
    );

    return (
      <div className='chat-canvas'>
        {threadId && (
          <>
            <div className='canvas-assets-holder'>
              <AIReportingProvider>
                <CanvasAssetsWithReporting
                  threadId={threadId}
                  layoutType={layoutType}
                  discussingObject={discussingObject}
                  discussingObjectMatch={discussingObjectMatch}
                  isMainThread={isMainThread}
                  isChannelThread={isChannelThread}
                  isOnboardingThread={isOnboardingThread}
                  isInboundEmailThread={isInboundEmailThread}
                  agentType={agentType}
                  boundProDocs={boundProDocs}
                  subThreads={subThreads}
                  projectApps={projectApps}
                  threadDescription={threadDescription}
                  layoutLoading={layoutLoading}
                  onHasAssets={onHasAssets}
                  onMobileBack={onMobileBack}
                  onMobileOpen={onMobileOpen}
                  hideMobileChatButton={hideMobileChatButton}
                  iconImageUrl={iconImageUrl}
                  ref={aiReportingRef}
                />
              </AIReportingProvider>
            </div>
          </>
        )}
      </div>
    );
  },
);
ChatCanvas.displayName = 'ChatCanvas';

export default ChatCanvas;
